import {
  Button,
  type ButtonProps,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  DictionaryIcon,
  SearchField,
  Show,
  SlidingPanel,
  useIsMobile,
  useLocalStorageState,
} from '@side/pantry';
import { useEffect, useMemo, useState } from 'react';
import { useReportContext } from '../../context/ReportContext';
import { SHOW_INLINE_GLOSSARY_DEFINITIONS_KEY } from './constants';
import { GlossaryAccordion } from './GlossaryAccordion';
import { GlossaryItem } from './GlossaryItem';
import { useGlossaryDefinitions } from './useGlossaryDefinitions';

export const GlossaryButton = ({
  showOnlyIcon,
  size,
  fullWidth,
  hiddenCategories,
}: {
  showOnlyIcon?: boolean;
  size?: ButtonProps['size'];
  fullWidth?: boolean;
  hiddenCategories?: string[];
}) => {
  const isMobile = useIsMobile('md');

  const { updateContext } = useReportContext();

  const glossaryDefinitions = useGlossaryDefinitions();
  const [searchValue, setSearchValue] = useState('');
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);

  const [showInlineGlossaryDefinitions, setShowInlineGlossaryDefinitions] =
    useLocalStorageState(SHOW_INLINE_GLOSSARY_DEFINITIONS_KEY, true);

  useEffect(() => {
    updateContext({ showInlineGlossaryDefinitions });
  }, [showInlineGlossaryDefinitions, updateContext]);

  const availableDefinitions = useMemo(
    () =>
      glossaryDefinitions.filter(
        ({ category }) => !hiddenCategories?.includes(category),
      ),
    [glossaryDefinitions, hiddenCategories],
  );

  const definitions = useMemo(() => {
    if (!searchValue) {
      return availableDefinitions;
    }

    return availableDefinitions
      .map(({ category, items }) => ({
        category,
        items: items.filter(({ title }) =>
          title.toLowerCase().includes(searchValue.toLowerCase()),
        ),
      }))
      .filter(({ items }) => items.length > 0);
  }, [availableDefinitions, searchValue]);

  const handleClear = () => {
    setSearchValue('');
  };

  const handleClose = () => {
    setIsGlossaryOpen(false);
    handleClear();
  };

  return (
    <>
      {showOnlyIcon ? (
        <IconButton
          id="glossary-button"
          aria-label="Glossary"
          onClick={() => setIsGlossaryOpen(true)}
          sx={(t) => ({
            borderRadius: '2px',
            border: `1.5px solid ${t.palette.other.outlineBorder}`,
            backgroundColor: t.palette.common.white,
            // Match filter/insights icon button heights for row alignment.
            height: size === 'small' ? '31px' : '40px',
            width: size === 'small' ? '31px' : '40px',
            '&:hover': {
              backgroundColor: t.palette.common.white,
            },
          })}
          size="small"
        >
          <DictionaryIcon fontSize="small" />
        </IconButton>
      ) : (
        <Button
          id="glossary-button"
          size={size}
          fullWidth={fullWidth ?? isMobile}
          variant="outlined"
          color="utility"
          startIcon={<DictionaryIcon />}
          sx={{
            minWidth: 0,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: 0.75,
            },
            '& .MuiButton-startIcon + *': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          onClick={() => setIsGlossaryOpen(true)}
        >
          Glossary
        </Button>
      )}
      <SlidingPanel open={isGlossaryOpen} onClose={handleClose} anchor="right">
        <Stack direction="column" sx={{ gap: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Key Terms
          </Typography>
          <Typography variant="h2" sx={{ color: 'text.secondary' }}>
            Side App Glossary
          </Typography>
        </Stack>
        <SearchField
          placeholder="Search by keyword"
          handleSearchChange={setSearchValue}
          value={searchValue}
          onClear={handleClear}
          onChange={(e) => setSearchValue(e.target.value)}
          sx={{ py: 2 }}
          fullWidth
        />
        <Stack direction="column" sx={{ gap: 1.25 }}>
          <Show
            when={definitions.length > 0}
            fallback={
              <Typography sx={{ color: 'text.secondary' }}>
                No matching results
              </Typography>
            }
          >
            {definitions.map(({ category, items }) => (
              <GlossaryAccordion
                key={category}
                title={category}
                hasMatchWithSearchTerm={!!searchValue}
              >
                {items.map(({ title, definition }) => (
                  <GlossaryItem
                    key={title}
                    title={title}
                    definition={definition}
                  />
                ))}
              </GlossaryAccordion>
            ))}
          </Show>
          <FormControlLabel
            sx={{ pt: 1, pl: 1, color: 'text.secondary' }}
            control={
              <Switch
                checked={showInlineGlossaryDefinitions}
                onChange={(e) =>
                  setShowInlineGlossaryDefinitions(e.target.checked)
                }
              />
            }
            label="Show inline definitions"
          />
        </Stack>
      </SlidingPanel>
    </>
  );
};
